import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface Chemical { chemical_id: number; chemical_name: string; chemical_measure: string; chemical_epa: string; chemical_notes: string; }
interface Unit { code: string; label: string; }

const API = environment.api + 'chemicals';

// Code Text line breaks: stored as <br> (as twin-ci's rich-text box saved them), shown and edited
// as plain line breaks.
const fromHtml = (text: string) => (text || '').replace(/<br\s*\/?>/gi, '\n');
const toHtml = (text: string) => (text || '').replace(/\r?\n/g, '<br>');

/**
 * Settings > Chemicals (moved from twin-ci chemicals_list / add / edit / del; mockup settings
 * "Chemicals"). Every chemical is listed with a search, as the mockup (agreed); add and edit in a
 * dialog. Kept from twin-ci: code and EPA # saved upper-cased, duplicates allowed, delete is soft
 * (job history keeps the chemical). Units come from config/chemicals.php through the API.
 */
@Component({
    selector: 'app-chemicals',
    templateUrl: './chemicals.component.html',
    styleUrls: ['./chemicals.component.scss']
})
export class ChemicalsComponent implements OnInit {
    chemicals: Chemical[] = [];
    units: Unit[] = [];
    loaded = false;
    search = '';
    message = '';
    error = '';

    editing: Chemical = null;     // the dialog's record; chemical_id 0 = new
    saving = false;
    formError = '';
    fieldErrors: { [field: string]: string } = {};
    deleting: Chemical = null;

    constructor(private http: HttpClient) { }

    ngOnInit() {
        this.load();
    }

    private load() {
        this.http.get<{ data: Chemical[], units: Unit[] }>(API + '/settings').subscribe(
            res => {
                // twin-ci's Code Text was a rich-text box: its line breaks are saved as <br>.
                this.chemicals = (res.data || []).map(c => Object.assign(c, { chemical_notes: fromHtml(c.chemical_notes) }));
                this.units = res.units || [];
                this.loaded = true;
            },
            err => this.error = this.text(err, 'The chemicals could not be loaded.')
        );
    }

    get shown(): Chemical[] {
        const q = this.search.trim().toLowerCase();
        return !q ? this.chemicals : this.chemicals.filter(c =>
            [c.chemical_name, c.chemical_measure, c.chemical_epa, c.chemical_notes].join(' ').toLowerCase().indexOf(q) !== -1);
    }

    /** A saved unit outside the list (older data) stays selectable while editing. */
    get unitOptions(): Unit[] {
        const code = this.editing && this.editing.chemical_measure;
        return !code || this.units.some(u => u.code === code) ? this.units : this.units.concat([{ code, label: 'not in the list' }]);
    }

    add() {
        this.open({ chemical_id: 0, chemical_name: '', chemical_measure: '', chemical_epa: '', chemical_notes: '' });
    }

    edit(c: Chemical) {
        this.open(Object.assign({}, c));
    }

    private open(c: Chemical) {
        this.editing = c;
        this.formError = '';
        this.fieldErrors = {};
        this.message = '';
    }

    close() {
        this.editing = null;
    }

    save() {
        const c = this.editing;
        this.fieldErrors = {};
        if (!c.chemical_name.trim()) { this.fieldErrors.chemical_name = 'Enter the chemical code.'; }
        if (!c.chemical_measure) { this.fieldErrors.chemical_measure = 'Select the units.'; }
        if (!c.chemical_epa.trim()) { this.fieldErrors.chemical_epa = 'Enter the EPA registration #.'; }
        if (Object.keys(this.fieldErrors).length) {
            return;
        }
        const body = { chemical_name: c.chemical_name, chemical_measure: c.chemical_measure, chemical_epa: c.chemical_epa, chemical_notes: toHtml(c.chemical_notes) };
        const request = c.chemical_id ? this.http.put<any>(API + '/' + c.chemical_id, body) : this.http.post<any>(API, body);
        this.saving = true;
        this.formError = '';
        request.subscribe(
            res => {
                this.saving = false;
                this.editing = null;
                this.message = res.message;
                this.load();
            },
            err => {
                this.saving = false;
                this.formError = this.text(err, 'The chemical could not be saved.');
            }
        );
    }

    confirmDelete(c: Chemical) {
        this.deleting = c;
        this.message = '';
    }

    remove() {
        const c = this.deleting;
        this.saving = true;
        this.http.delete<any>(API + '/' + c.chemical_id).subscribe(
            res => {
                this.saving = false;
                this.deleting = null;
                this.message = res.message;
                this.load();
            },
            err => {
                this.saving = false;
                this.deleting = null;
                this.error = this.text(err, 'The chemical could not be deleted.');
            }
        );
    }

    // API errors: Laravel's { message, errors } or the error interceptor's string.
    private text(err: any, fallback: string): string {
        if (typeof err === 'string') {
            return err;
        }
        const e = err && err.error;
        if (e && e.errors) {
            const first = Object.keys(e.errors)[0];
            return e.errors[first][0];
        }
        return (e && e.message) || fallback;
    }
}
